import { Check, ChevronRight } from "lucide-react";
/**
 * Starter checklist ("Getting started · 3 of 5 · next: place a schematic"): a compact button at the bottom of the plan
 * sidebar that opens a popover with the five bigger features. Each item ticks itself when it happens
 * (lib/onboarding.ts `track`); "Show me" opens the feature. `compact` = only the progress ring (collapsed 56 px rail).
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { controller } from "../../app/controller.ts";
import { useApp } from "../../state/store.ts";
import { CHECKLIST, nextChecklistItem, useOnboarding, type ChecklistItem } from "../../lib/onboarding.ts";

function showMe(id: ChecklistItem): string | null {
  const s = useApp.getState();
  const sel = s.plans.find((p) => p.id === s.selectedId);
  switch (id) {
    case "draw": s.set({ tool: "path" }); return null;
    case "layout":
      if (sel && (sel.kind === "area" || sel.kind === "path")) { controller.startFill(sel.id); return null; }
      controller.openLayouts("fill");
      return "Select an area (or a path) first, then “Fill with a layout…” in its panel.";
    case "schematic": controller.openLayouts("schematics"); return null;
    case "board":
      if (sel) { controller.openBoard(sel.id); return null; }
      return "Click something on the map first, then Pinboard in its panel.";
    case "together":
      (document.querySelector(".collab-btn") as HTMLButtonElement | null)?.click();
      return s.demo ? "The demo island stays on this computer. Open your own world to plan together." : null;
  }
}

const popoverSupported = typeof HTMLElement !== "undefined" && "popover" in HTMLElement.prototype;

/** 28 px progress ring in green (n of total) */
function Ring({ n, total, size = 28 }: { n: number; total: number; size?: number }) {
  const r = 11, c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox="0 0 28 28" aria-hidden="true" className="ring">
      <circle cx="14" cy="14" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
      {n > 0 && <circle cx="14" cy="14" r={r} fill="none" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round" strokeDasharray={`${(c * n) / total} ${c}`} transform="rotate(-90 14 14)" />}
    </svg>
  );
}

export function Checklist({ compact = false }: { compact?: boolean }) {
  const phase = useApp((s) => s.phase);
  const tourStep = useOnboarding((s) => s.tourStep);
  const hidden = useOnboarding((s) => s.checklistHidden || !s.tourDone);
  const done = useOnboarding((s) => s.checklist);
  const [open, setOpen] = useState(false);
  const [tip, setTip] = useState<string | null>(null);
  const [at, setAt] = useState<React.CSSProperties>({});
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  /** when light dismiss closed the popover (a click on the button right after must not reopen it) */
  const closedAt = useRef(0);
  const visible = !(phase !== "ready" || hidden || tourStep !== null);

  // place the popover above the button (or beside the ring in the collapsed rail), inside the window
  useLayoutEffect(() => {
    if (!open || !trigger.current) return;
    const place = () => {
      const r = trigger.current!.getBoundingClientRect();
      const w = Math.min(320, window.innerWidth - 16);
      const left = Math.max(8, Math.min(window.innerWidth - w - 8, compact ? r.right + 8 : r.left));
      const bottom = Math.max(8, compact ? window.innerHeight - r.bottom : window.innerHeight - r.top + 8);
      setAt({ left, bottom, width: w });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open, compact]);

  // top layer + light dismiss where the Popover API exists; otherwise a fixed panel closed by Esc / outside clicks
  useEffect(() => {
    const el = panel.current;
    if (!open || !el) return;
    if (popoverSupported) {
      try { el.showPopover(); } catch { /* already open */ }
      const onToggle = (e: Event) => { if ((e as ToggleEvent).newState === "closed") { closedAt.current = performance.now(); setOpen(false); } };
      el.addEventListener("toggle", onToggle);
      return () => { el.removeEventListener("toggle", onToggle); try { el.hidePopover(); } catch { /* closed */ } };
    }
    const onDown = (e: PointerEvent) => { if (!el.contains(e.target as Node) && !trigger.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("pointerdown", onDown, true); window.removeEventListener("keydown", onKey); };
  }, [open]);

  useEffect(() => { if (!visible) setOpen(false); }, [visible]);
  if (!visible) return null;

  const n = CHECKLIST.filter((i) => done[i.id]).length;
  const total = CHECKLIST.length;
  const all = n === total;
  const next = nextChecklistItem(done);
  const hide = () => { setOpen(false); useOnboarding.getState().set({ checklistHidden: true }); };
  const summary = all ? `All ${total} done` : `${n} of ${total} · next: ${next!.label.charAt(0).toLowerCase()}${next!.label.slice(1)}`;
  // a press on the button light-dismisses the open popover first: that press only closes it
  const toggle = () => { if (!open && performance.now() - closedAt.current < 400) return; setOpen(!open); setTip(null); };

  return (
    <div className={`checklist${compact ? " compact" : ""}${open ? " open" : ""}`}>
      <button
        ref={trigger}
        type="button"
        className="checklist-pill"
        onClick={toggle}
        aria-expanded={open}
        aria-label={compact ? `Getting started: ${n} of ${total}` : undefined}
        title={compact ? `Getting started · ${n} of ${total}` : undefined}
      >
        <Ring n={n} total={total} size={compact ? 24 : 28} />
        {!compact && (
          <>
            <span className="checklist-text"><b>Getting started</b><span>{summary}</span></span>
            <ChevronRight size={14} aria-hidden="true" className="chev" />
          </>
        )}
      </button>
      {open && (
        <div
          ref={panel}
          className="checklist-panel"
          role="region"
          aria-label="Getting started"
          popover={popoverSupported ? "auto" : undefined}
          style={{ position: "fixed", top: "auto", right: "auto", margin: 0, ...at }}
        >
          <div className="checklist-head">
            <span className="grow">
              <b>{all ? "All done. Nice planning." : "Getting started"}</b>
              <span className="caption tabular">{n} of {total}</span>
            </span>
            <button type="button" className="btn btn-sm btn-ghost" onClick={hide}>Hide</button>
          </div>
          <ul>
            {CHECKLIST.map((i) => (
              <li key={i.id} className={done[i.id] ? "done" : next?.id === i.id ? "next" : ""}>
                <span className="tick" aria-hidden="true">{done[i.id] ? <Check size={11} strokeWidth={3.2} aria-hidden="true" /> : null}</span>
                <span className="grow">
                  <b>{i.label}{done[i.id] && <span className="sr-only"> (done)</span>}</b>
                  <span className="hint">{i.hint}</span>
                </span>
                {!done[i.id] && <button type="button" className={`btn btn-sm ${next?.id === i.id ? "btn-outline" : "btn-ghost"}`} onClick={() => { const t = showMe(i.id); setTip(t); if (!t) setOpen(false); }}>Show me</button>}
              </li>
            ))}
          </ul>
          {tip && <p className="checklist-tip" role="status">{tip}</p>}
          <p className="caption checklist-foot">Help (<kbd>?</kbd>), then “Show getting started” brings this back.</p>
        </div>
      )}
    </div>
  );
}
